import { useEffect, memo, useMemo } from 'react';
import { Marker, useMap, MapContainer, TileLayer } from 'react-leaflet';
import { ParseCoordinates } from '../Utils/CountryUtility';
import L from 'leaflet';

type LatLon = [number, number];

interface PostItem {
  coordinates: string;
}

interface ItemRefs {
  current: Array<HTMLElement | null>;
}

interface UpdateMapViewProps {
  center: LatLon;
}

interface MemoizedMarkerProps {
  position: LatLon;
  icon: L.DivIcon;
  onClick: () => void;
}

type MarkerComponent = React.ComponentType<{
  position: LatLon;
  eventHandlers: { click: () => void };
  icon?: L.DivIcon;
}>;

const LeafletMarker = Marker as unknown as MarkerComponent;

interface SightseeingLeafletContainerProps {
  sightseeingData: PostItem[];
  mapCenter: LatLon;
  setActiveMarker: React.Dispatch<React.SetStateAction<number>>;
  activeMarker: number;
  setActivePost: React.Dispatch<React.SetStateAction<number>>;
  itemRefs: ItemRefs;
}

export const UpdateMapView = ({ center }: UpdateMapViewProps): null => {
  const map = useMap();
  useEffect(() => {
    map.setView(center);
  }, [center, map]);
  return null;
};

export const MapGestureControl = (): null => {
  const map = useMap();

  useEffect(() => {
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;

    if (!isTouchDevice) {
      map.dragging.enable();
      return;
    }

    const holdDelayMs = 160;
    const moveThresholdPx = 12;

    const mapContainer = map.getContainer();
    let holdTimer: number | undefined;
    let startX = 0;
    let startY = 0;

    const clearHoldTimer = (): void => {
      if (holdTimer !== undefined) {
        window.clearTimeout(holdTimer);
        holdTimer = undefined;
      }
    };

    const onTouchStart = (event: TouchEvent): void => {
      if (event.touches.length >= 2) {
        clearHoldTimer();
        map.dragging.enable();
        return;
      }

      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      map.dragging.disable();

      clearHoldTimer();
      holdTimer = window.setTimeout(() => {
        map.dragging.enable();
      }, holdDelayMs);
    };

    const onTouchMove = (event: TouchEvent): void => {
      if (event.touches.length >= 2) {
        clearHoldTimer();
        map.dragging.enable();
        return;
      }

      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - startX);
      const deltaY = Math.abs(touch.clientY - startY);

      if (deltaX > moveThresholdPx || deltaY > moveThresholdPx) {
        clearHoldTimer();
      }
    };

    const onTouchEnd = (event: TouchEvent): void => {
      clearHoldTimer();
      if (event.touches.length < 2) {
        map.dragging.disable();
      }
    };

    map.dragging.disable();
    mapContainer.addEventListener('touchstart', onTouchStart, { passive: true });
    mapContainer.addEventListener('touchmove', onTouchMove, { passive: true });
    mapContainer.addEventListener('touchend', onTouchEnd, { passive: true });
    mapContainer.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      clearHoldTimer();
      mapContainer.removeEventListener('touchstart', onTouchStart);
      mapContainer.removeEventListener('touchmove', onTouchMove);
      mapContainer.removeEventListener('touchend', onTouchEnd);
      mapContainer.removeEventListener('touchcancel', onTouchEnd);
      map.dragging.enable();
    };
  }, [map]);

  return null;
};

export const MemoizedMarker = memo(({ position, icon, onClick }: MemoizedMarkerProps) => {
  const eventHandlers = useMemo(
    () => ({
      click: onClick,
    }),
    [onClick],
  );
  return <LeafletMarker position={position} icon={icon} eventHandlers={eventHandlers} />;
});

export const HandleMarkerItemClick = (
  index: number,
  setActiveMarker: React.Dispatch<React.SetStateAction<number>>,
  setActivePost: React.Dispatch<React.SetStateAction<number>>,
  itemRefs: ItemRefs,
): void => {
  // Set the clicked marker as active
  setActiveMarker(index);
  setActivePost(index); // Set the active post

  // Scroll the clicked item into view
  itemRefs.current?.[index]?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};

export const CreateCustomIcon = (isActive: boolean): L.DivIcon =>
  L.divIcon({
    className: `custom-marker ${isActive ? 'active' : ''}`, // Add active class when active
    iconSize: [30, 30],
    iconAnchor: [15, 30], // Anchor point at the bottom center
  });

export const SightseeingLeafletContainer = ({ sightseeingData, mapCenter, setActiveMarker, activeMarker, setActivePost, itemRefs }: SightseeingLeafletContainerProps): JSX.Element | null => {
  if (!Array.isArray(sightseeingData)) return null;

  return (
    <div className="LeafletContainer">
      <div className="map-gesture-hint">
        Scroll normally. Hold on map or use two fingers to drag.
      </div>
      <MapContainer center={mapCenter} zoom={5} style={{ height: '300px', width: '300px' }}>
        <MapGestureControl />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
        <UpdateMapView center={mapCenter} />
        {sightseeingData.map((post, index) => {
          const [lat, lon] = ParseCoordinates(post.coordinates);
          if (!isNaN(lat) && !isNaN(lon)) {
            return (
              <MemoizedMarker
                key={index}
                position={[lat, lon]}
                icon={CreateCustomIcon(activeMarker === index)}
                onClick={() => HandleMarkerItemClick(index, setActiveMarker, setActivePost, itemRefs)}
              />
            );
          }
          return null;
        })}
      </MapContainer>
    </div>
  );
};
